import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { formatDateTime } from "../../lib/missions";
import { RESULT_META, conditionOf, groupAssets, issueDraftFromCheck, lastCheckIn, parkSummary } from "../../lib/equipment";
import { AssetDialog, CheckDialog, IssueDialog } from "./EquipmentDialogs";
import IssueDetailSheet, { IssueStatusBadge, SeverityBadge } from "./IssueDetailSheet";
import { useEquipmentCategories } from "./useEquipmentCategories";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { ResponsiveDropdownMenu, ResponsiveDropdownMenuContent, ResponsiveDropdownMenuItem, ResponsiveDropdownMenuTrigger } from "../ui/responsive-dropdown-menu";
import { AlertTriangle, MoreHorizontal, Plus, Printer } from "lucide-react";
import { toast } from "sonner";

export function ConditionDot({ asset }) {
  const c = conditionOf(asset);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-2xs font-medium ${c.className}`} data-testid={`equipment-condition-${asset.id}`}>
      <span className={`h-2 w-2 rounded-full ${c.dot}`} aria-hidden />{c.label}
    </span>
  );
}

// « Matériel » card of a mission: the park of the mission's pharmacy, what was tested during THIS mission, the open issues.
// The team tests from its phone (one « Tester » per device); a faulty result proposes an issue, never opens one silently.
// Everything shown and every action offered comes from GET /missions/{id}/equipment (`capabilities`).
export default function MissionEquipmentCard({ mission, userId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // {kind: "add"|"edit"|"check"|"issue", asset?, corrects?, draft?}
  const [issueId, setIssueId] = useState(null);
  const categories = useEquipmentCategories();

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/missions/${mission.id}/equipment`);
      setData(d);
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger le matériel."));
    }
  }, [mission.id]);
  useEffect(() => { load(); }, [load, mission.status]);

  if (error && !data) {
    return <Card className="p-5 text-sm text-slate-600 flex items-center gap-2 flex-wrap" data-testid="mission-equipment">{error}<Button variant="outline" size="sm" onClick={load}>Réessayer</Button></Card>;
  }
  if (!data) return <Card className="p-5"><Skeleton className="h-16 w-full" /></Card>;

  const caps = data.capabilities;
  const { groups, retired } = groupAssets(data.assets);
  const summary = parkSummary(data.assets, data.checks);
  const openIssues = data.issues.filter((i) => ["open", "in_progress"].includes(i.status));
  const close = () => setDialog(null);

  const afterCheck = (d, { asset, result }) => {
    setData(d);
    close();
    if (["degraded", "out_of_service"].includes(result) && caps.open_issue) {
      const check = lastCheckIn(d.checks, asset.id);
      const fresh = d.assets.find((a) => a.id === asset.id) || asset;
      toast.warning(`${asset.label} : ${RESULT_META[result].label.toLowerCase()}`, {
        action: { label: "Ouvrir une anomalie", onClick: () => setDialog({ kind: "issue", draft: issueDraftFromCheck(fresh, check) }) },
        duration: 10000,
      });
    } else {
      toast.success("Contrôle enregistré");
    }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="mission-equipment">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Printer size={16} className="text-slate-400" />Matériel</h3>
          {summary.total > 0 && (
            <p className="text-xs text-slate-500" data-testid="mission-equipment-summary">
              {summary.checked} / {summary.total} contrôlé{summary.checked > 1 ? "s" : ""} pendant la mission{summary.faulty > 0 ? ` · ${summary.faulty} en défaut` : ""}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {caps.open_issue && <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "issue", draft: null })} data-testid="mission-equipment-new-issue">Signaler une anomalie</Button>}
          {caps.add_asset && <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "add" })} data-testid="mission-equipment-add"><Plus size={14} className="mr-1" />Ajouter</Button>}
        </div>
      </div>

      {openIssues.length > 0 && (
        <ul className="space-y-1.5" data-testid="mission-equipment-issues">
          {openIssues.map((i) => (
            <li key={i.id}>
              <button type="button" onClick={() => setIssueId(i.id)} data-testid={`mission-equipment-issue-${i.id}`}
                className="w-full rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-left flex items-start gap-2 hover:bg-amber-50">
                <AlertTriangle size={15} className="text-amber-700 mt-0.5 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900 break-words">{i.title}</span>
                  <span className="block text-xs text-slate-600">{i.number}{i.asset_label ? ` · ${i.asset_label}` : ""} · {i.responsible_name}</span>
                </span>
                <span className="flex flex-col items-end gap-1 shrink-0"><SeverityBadge severity={i.severity} /><IssueStatusBadge status={i.status} /></span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {groups.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="mission-equipment-empty">
          Aucun matériel recensé pour cette pharmacie.{caps.add_asset ? " Ajoutez les ordinateurs, imprimantes et lecteurs que vous trouvez sur place." : ""}
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <section key={g.label}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{g.label}</h4>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {g.items.map((a) => (
                  <AssetRow key={a.id} asset={a} mission={mission} check={lastCheckIn(data.checks, a.id)} caps={caps}
                    onCheck={() => setDialog({ kind: "check", asset: a })} onEdit={() => setDialog({ kind: "edit", asset: a })}
                    onCorrect={(c) => setDialog({ kind: "check", asset: a, corrects: c })} />
                ))}
              </ul>
            </section>
          ))}
          {retired.length > 0 && <p className="text-xs text-slate-500">{retired.length} équipement{retired.length > 1 ? "s" : ""} retiré{retired.length > 1 ? "s" : ""} (historique).</p>}
        </div>
      )}

      <AssetDialog open={dialog?.kind === "add"} onOpenChange={close} categories={categories} url={`/missions/${mission.id}/equipment`} serialAllowed={caps.add_asset}
        onDone={(d) => { setData(d); close(); }} />
      <AssetDialog open={dialog?.kind === "edit"} onOpenChange={close} categories={categories} asset={dialog?.asset} url={dialog?.asset ? `/equipment/${dialog.asset.id}` : ""}
        method="patch" extra={{ mission_id: mission.id }} serialAllowed={!!dialog?.asset && "serial_number" in dialog.asset} onDone={() => { close(); load(); }} />
      <CheckDialog open={dialog?.kind === "check"} onOpenChange={close} mission={mission} asset={dialog?.asset} corrects={dialog?.corrects}
        performers={data.performers} userId={userId} onDone={afterCheck} />
      <IssueDialog open={dialog?.kind === "issue"} onOpenChange={close} missionId={mission.id} assets={data.assets} candidates={data.responsible_candidates}
        userId={userId} draft={dialog?.draft} onDone={(issue) => { close(); load(); setIssueId(issue.id); }} />
      <IssueDetailSheet issueId={issueId} onOpenChange={(open) => { if (!open) setIssueId(null); }} onChanged={load} />
    </Card>
  );
}

function AssetRow({ asset, mission, check, caps, onCheck, onEdit, onCorrect }) {
  const title = [asset.manufacturer, asset.model].filter(Boolean).join(" ") || asset.category_label;
  return (
    <li className="p-3 flex items-start gap-3" data-testid={`mission-equipment-asset-${asset.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900 break-words">{title}</div>
        <div className="text-xs text-slate-500 break-words">
          {asset.location || "Emplacement non précisé"}{asset.serial_number ? ` · N° ${asset.serial_number}` : ""}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <ConditionDot asset={asset} />
          {check ? (
            <span className="text-2xs text-slate-500" data-testid={`mission-equipment-checked-${asset.id}`}>
              Testé le {formatDateTime(check.checked_at, mission.timezone)} · {check.performed_by.name}{check.files.length ? ` · ${check.files.length} photo${check.files.length > 1 ? "s" : ""}` : ""}
            </span>
          ) : asset.last_check ? <span className="text-2xs text-slate-500">Dernier contrôle : {formatDateTime(asset.last_check.at, mission.timezone)}</span> : null}
        </div>
      </div>
      {caps.record_check && (
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant={check ? "outline" : "default"} className="h-10" onClick={onCheck} data-testid={`mission-equipment-test-${asset.id}`}>
            {check ? "Retester" : "Tester"}
          </Button>
          <ResponsiveDropdownMenu>
            <ResponsiveDropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10" aria-label={`Autres actions : ${asset.label}`} data-testid={`mission-equipment-more-${asset.id}`}>
                <MoreHorizontal size={16} />
              </Button>
            </ResponsiveDropdownMenuTrigger>
            <ResponsiveDropdownMenuContent align="end">
              {caps.edit_asset && <ResponsiveDropdownMenuItem onClick={onEdit}>Modifier l'équipement</ResponsiveDropdownMenuItem>}
              {check && <ResponsiveDropdownMenuItem onClick={() => onCorrect(check)} data-testid={`mission-equipment-correct-${asset.id}`}>Corriger le dernier contrôle</ResponsiveDropdownMenuItem>}
            </ResponsiveDropdownMenuContent>
          </ResponsiveDropdownMenu>
        </div>
      )}
    </li>
  );
}
